import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlaygroundPosition } from '../../pages/PlaygroundPage/PlaygroundPage';
import './SetupPopup.css';

interface SetupSettings {
  smallBlind: number;
  bigBlind: number;
  maxPlayers: number;
  heroPosition: PlaygroundPosition;
}

interface SetupPopupProps {
  isVisible: boolean;
  onConfirm: (settings: SetupSettings) => void;
  onCancel: () => void;
  initialSettings: SetupSettings;
}

const SetupPopup: React.FC<SetupPopupProps> = ({
  isVisible,
  onConfirm,
  onCancel,
  initialSettings
}) => {
  const [settings, setSettings] = useState<SetupSettings>(initialSettings);

  useEffect(() => {
    if (isVisible) {
      setSettings(initialSettings);
    }
  }, [isVisible, initialSettings]);

  const handleInputChange = (field: keyof SetupSettings, value: string | number) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfirm = () => {
    onConfirm(settings);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isVisible) return null;

  const positions: PlaygroundPosition[] = ['UTG', 'UTG+1', 'UTG+2', 'MP', 'LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
  const playerCounts = [2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <AnimatePresence>
      <motion.div
        className="setup-popup-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
      >
        <motion.div
          className="setup-popup-content"
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          <div className="setup-popup-header">
            <h3>Setup Hand</h3>
            <button className="close-btn" onClick={onCancel}>×</button>
          </div>
          
          <div className="setup-popup-body">
            <div className="setup-section">
              <label>Stakes</label>
              <div className="stakes-inputs">
                <div className="stake-input-group">
                  <span className="stake-label">Small Blind</span>
                  <div className="stake-input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      value={settings.smallBlind}
                      onChange={(e) => handleInputChange('smallBlind', parseFloat(e.target.value) || 0)}
                      min={0.01}
                      step="0.01"
                      className="stake-input"
                    />
                  </div>
                </div>
                <div className="stake-input-group">
                  <span className="stake-label">Big Blind</span>
                  <div className="stake-input-wrapper">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      value={settings.bigBlind}
                      onChange={(e) => handleInputChange('bigBlind', parseFloat(e.target.value) || 0)}
                      min={0.01}
                      step="0.01"
                      className="stake-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="setup-section">
              <label>Table Settings</label>
              <div className="table-settings">
                <div className="setting-group">
                  <span className="setting-label">Players</span>
                  <select
                    value={settings.maxPlayers}
                    onChange={(e) => handleInputChange('maxPlayers', parseInt(e.target.value))}
                    className="setting-select"
                  >
                    {playerCounts.map(count => (
                      <option key={count} value={count}>{count} Players</option>
                    ))}
                  </select>
                </div>
                <div className="setting-group">
                  <span className="setting-label">Hero Position</span>
                  <select
                    value={settings.heroPosition}
                    onChange={(e) => handleInputChange('heroPosition', e.target.value as PlaygroundPosition)}
                    className="setting-select"
                  >
                    {positions.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="setup-instructions">
              <p>Configure table settings and blinds</p>
              <ul>
                <li>Set the small and big blind amounts</li>
                <li>Choose number of players (2-9)</li>
                <li>Select your hero position</li>
                <li>Click "Start Input" to begin hand creation</li>
              </ul>
            </div>

            <div className="action-buttons">
              <button className="cancel-btn" onClick={onCancel}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleConfirm}>
                Start Input
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SetupPopup;